import React, { useState, useEffect } from 'react';
import { getEmailLog } from '../services/api';

const COVAGE_ADDRS = ['service.delivery@covage.com', 'supplier-in-test@covage.com', 'supplier-in@covage.com', 'support-access@covage.com'];

function isCovage(to) {
  return Array.isArray(to) && to.some(a => COVAGE_ADDRS.includes(a));
}

function TypeBadge({ type, resultat }) {
  const colors = {
    CRI: resultat === 'CLEARED' ? '#22c55e' : resultat === 'FAILED' ? '#ef4444' : 'var(--blue2)',
    VT: '#8b5cf6',
    SAV: '#f59e0b',
  };
  return (
    <span style={{ background: (colors[type] || 'var(--blue2)') + '22', color: colors[type] || 'var(--blue2)', border: `1px solid ${colors[type] || 'var(--blue2)'}44`, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      {type}{resultat ? ` ${resultat}` : ''}
    </span>
  );
}

function openPdfInTab(pdfUrl) {
  const token = localStorage.getItem('admin_token');
  const clean = pdfUrl.split('?')[0];
  const url = clean.startsWith('/api') ? clean : `/api${clean}`;
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    });
}

function EmailPreviewModal({ entry, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entry?.cr_id) return;
    const token = localStorage.getItem('admin_token');
    const forfait = entry.forfait || 'Aucun';
    fetch(`/api/admin/cr/${entry.cr_id}/email-preview?forfait=${encodeURIComponent(forfait)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setPreview(data))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, [entry]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700, width: '95%' }}>
        <div className="modal-header">
          <div className="modal-title">Aperçu de l'email envoyé</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>
          ) : !preview ? (
            <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Impossible de charger l'aperçu</div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Objet</div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)' }}>{preview.subject || entry.subject || '—'}</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Destinataires</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(preview.to || entry.to || []).map((addr, i) => {
                    const cov = COVAGE_ADDRS.includes(addr);
                    return (
                      <span key={i} style={{ fontSize: 12, color: cov ? '#22c55e' : 'var(--text2)', fontWeight: cov ? 700 : 400 }}>
                        {cov ? '🏢 ' : '🏠 '}{addr}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Corps de l'email</div>
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'var(--mono)', color: 'var(--text1)' }}>
                  {preview.body || '—'}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default function EmailLog() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDest, setFilterDest] = useState('all');
  const [previewEntry, setPreviewEntry] = useState(null);

  useEffect(() => {
    getEmailLog().then(setLog).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = log.filter(e => {
    if (search && !e.ref_commande?.toLowerCase().includes(search.toLowerCase()) &&
        !e.ref_work_order?.toLowerCase().includes(search.toLowerCase()) &&
        !e.subject?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType !== 'all' && e.type_cr !== filterType) return false;
    if (filterDest === 'covage' && !isCovage(e.to)) return false;
    if (filterDest === 'internal' && isCovage(e.to)) return false;
    if (filterDest === 'error' && e.success !== false) return false;
    return true;
  });

  const countCovage = log.filter(e => isCovage(e.to) && e.success).length;
  const countInternal = log.filter(e => !isCovage(e.to) && e.success).length;
  const countErrors = log.filter(e => !e.success).length;

  const btnStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' };

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Emails CR envoyés</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>{log.length} email(s) dans l'historique</div>
      </div>

      <div className="page">
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { lbl: 'Total', val: log.length, color: 'var(--blue2)' },
            { lbl: 'Envoyés Covage', val: countCovage, color: '#22c55e' },
            { lbl: 'Interne seulement', val: countInternal, color: '#f59e0b' },
            { lbl: 'Erreurs', val: countErrors, color: '#ef4444' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
              <div className="lbl">{s.lbl}</div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ width: 260 }} placeholder="Rechercher ref, objet..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-input form-select" style={{ width: 140 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">Tous types</option>
            <option value="CRI">CRI</option>
            <option value="VT">CRVT</option>
            <option value="SAV">CRSAV</option>
          </select>
          <select className="form-input form-select" style={{ width: 200 }} value={filterDest} onChange={e => setFilterDest(e.target.value)}>
            <option value="all">Tous destinataires</option>
            <option value="covage">Envoyés à Covage</option>
            <option value="internal">Interne seulement</option>
            <option value="error">Erreurs d'envoi</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty"><div className="icon">✉</div><p>Aucun email trouvé</p></div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {['Date / Heure', 'Réf commande', 'Type', 'Objet', 'Raccordement', 'Forfait TVX', 'Destinataires', 'Statut', 'PDF', 'Aperçu mail'].map(h => (
                      <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, i) => {
                    const covage = isCovage(e.to);
                    return (
                      <tr key={i} style={{ background: !e.success ? 'rgba(239,68,68,0.04)' : undefined }}>
                        <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                          {e.at ? new Date(e.at).toLocaleString('fr-FR') : '—'}
                        </td>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>
                          <div>{e.ref_commande || '—'}</div>
                          {e.ref_work_order && <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>{e.ref_work_order}</div>}
                        </td>
                        <td>
                          <TypeBadge type={e.type_cr || 'CRI'} resultat={e.resultat} />
                          {/* Deux lignes identiques sans cette marque, et on ne
                              sait plus laquelle est le renvoi. */}
                          {e.renvoi && (
                            <div title={e.par ? 'Renvoyé par ' + e.par : 'Renvoi manuel'}
                              style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, fontWeight: 600 }}>
                              ↻ renvoi
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 260 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.subject}>{e.subject || '—'}</div>
                        </td>
                        <td>
                          {e.type_raccordement
                            ? <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{e.type_raccordement}</span>
                            : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                        </td>
                        <td>
                          {e.forfait && e.forfait !== 'Aucun'
                            ? <span style={{ background: e.forfait === 'DEVIS' ? 'rgba(133,79,11,0.12)' : 'rgba(34,197,94,0.12)', color: e.forfait === 'DEVIS' ? '#854F0B' : '#15803d', border: `1px solid ${e.forfait === 'DEVIS' ? 'rgba(133,79,11,0.3)' : 'rgba(34,197,94,0.3)'}`, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{e.forfait}</span>
                            : <span style={{ color: 'var(--text3)', fontSize: 12 }}>Aucun</span>}
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {e.success === false ? (
                            <span style={{ color: '#ef4444', fontSize: 12 }}>—</span>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {(e.to || []).map((addr, j) => {
                                const isCov = COVAGE_ADDRS.includes(addr);
                                return (
                                  <span key={j} style={{ fontSize: 11, color: isCov ? '#22c55e' : 'var(--text2)', fontWeight: isCov ? 600 : 400 }}>
                                    {isCov ? '🏢 ' : '🏠 '}{addr}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td>
                          {e.success === false ? (
                            <div>
                              <span style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✕ Erreur</span>
                              {e.error && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, maxWidth: 180 }}>{e.error}</div>}
                            </div>
                          ) : covage ? (
                            <span style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✓ Covage</span>
                          ) : (
                            <span style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706', border: '1px solid rgba(245,158,11,0.3)', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>⚠ Interne</span>
                          )}
                        </td>
                        <td>
                          {e.pdf_url ? (
                            <button onClick={() => openPdfInTab(e.pdf_url)} style={btnStyle}>
                              📄 PDF
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td>
                          {e.cr_id ? (
                            <button onClick={() => setPreviewEntry(e)} style={{ ...btnStyle, color: 'var(--blue2)', borderColor: 'var(--blue2)' }}>
                              👁 Aperçu
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {previewEntry && <EmailPreviewModal entry={previewEntry} onClose={() => setPreviewEntry(null)} />}
    </>
  );
}
